import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../helpers/api";
import { styled } from "@mui/material/styles";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import AppTheme from "../shared-theme/AppTheme";
import MuiCard from "@mui/material/Card";
import CssBaseline from "@mui/material/CssBaseline";
import Stack from "@mui/material/Stack";
import RequestVerification from "../components/RequestVerification";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  [theme.breakpoints.up("sm")]: {
    width: "450px",
  },
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const GradientBackground = styled("div")({
  position: "fixed",
  inset: 0,
  background: "radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)",
  zIndex: -1,
});

const SignUpContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100dvh",
  padding: theme.spacing(2),
  overflowY: "auto",
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

export default function VerifyEmail(props: { disableCustomTheme?: boolean }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<
    "loading" | "success" | "error" | "info"
  >("loading");
  const [open, setOpen] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setVerificationSent(true);
    setOpen(false);
  };

  useEffect(() => {
    (async () => {
      setStatus("loading");
      if (!token) {
        setStatus("error");
        return;
      }

      await api
        .get(`/user/verify?token=${token}`)
        .then((response) => {
          setStatus("success");
        })
        .catch((error: any) => {
          console.log(error);
          if (error.response.status === 501) {
            setStatus("info");
          } else {
            setStatus("error");
          }
        });
    })();
  }, [token]);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <GradientBackground />
      <SignUpContainer direction="column" justifyContent="space-between">
        <Card variant="outlined" sx={{ textAlign: "center" }}>
          {status === "loading" && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {status === "success" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="h6">Verification Successful</Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {"Your email has been successfully verified."}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/login")}
              >
                Return to Login
              </Button>
            </>
          )}

          {status === "info" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="h6">Email already verified</Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {"Your email has already been verified."}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/login")}
              >
                Return to Login
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="h6">Verification Failed</Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 3 }}>
                The verification link has expired or is invalid.
              </Typography>
              {!verificationSent && (
                <Button variant="contained" fullWidth onClick={handleClickOpen}>
                  Request Verification Email
                </Button>
              )}
              {verificationSent && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate("/login")}
                >
                  Return to Login
                </Button>
              )}
              <RequestVerification open={open} handleClose={handleClose} />
            </>
          )}
        </Card>
      </SignUpContainer>
    </AppTheme>
  );
}
